/**
 * First/last-touch UTM capture for signup attribution. Public pages call
 * captureUtmTouch() on mount; the app shell (AppLayout) stamps the stored
 * touches onto the caller's organization once after signup. Everything here
 * is best-effort: attribution must never break a page over a storage error.
 */

const FIRST_KEY = 'cpg_first_touch';
const LAST_KEY = 'cpg_last_touch';
const STAMPED_KEY = 'cpg_attribution_stamped';

const UTM_PARAMS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'] as const;

export interface Touch {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
  referrer?: string;
  landing_page?: string;
  captured_at?: string;
}

export function captureUtmTouch(): void {
  try {
    const params = new URLSearchParams(window.location.search);
    const touch: Touch = {};
    let hasUtm = false;
    for (const key of UTM_PARAMS) {
      const value = params.get(key);
      if (value) {
        touch[key] = value.slice(0, 200);
        hasUtm = true;
      }
    }

    // Only a campaign-tagged visit may overwrite last-touch - otherwise any
    // plain revisit would clobber the real campaign data. The very first
    // visit records a first touch regardless so referrer/landing page of
    // organic signups aren't lost.
    const isFirstVisit = !localStorage.getItem(FIRST_KEY);
    if (!hasUtm && !isFirstVisit) return;

    touch.referrer = document.referrer ? document.referrer.slice(0, 300) : undefined;
    touch.landing_page = (window.location.pathname + window.location.search).slice(0, 300);
    touch.captured_at = new Date().toISOString();

    if (isFirstVisit) localStorage.setItem(FIRST_KEY, JSON.stringify(touch));
    if (hasUtm) localStorage.setItem(LAST_KEY, JSON.stringify(touch));
  } catch {
    // Storage unavailable (private mode etc.) - skip silently.
  }
}

export function getStoredAttribution(): { first_touch: Touch | null; last_touch: Touch | null } | null {
  try {
    const first = localStorage.getItem(FIRST_KEY);
    const last = localStorage.getItem(LAST_KEY);
    if (!first && !last) return null;
    return {
      first_touch: first ? (JSON.parse(first) as Touch) : null,
      last_touch: last ? (JSON.parse(last) as Touch) : null,
    };
  } catch {
    return null;
  }
}

export function isAttributionStamped(): boolean {
  try {
    return localStorage.getItem(STAMPED_KEY) === '1';
  } catch {
    return true;
  }
}

export function markAttributionStamped(): void {
  try {
    localStorage.setItem(STAMPED_KEY, '1');
  } catch {
    // ignore
  }
}
